import { apiClient } from './api';
import type {
  Exercise,
  CreateExerciseInput,
  ApiCollection,
  ApiErrorBody,
  ExerciseRecordsResponse,
  WorkoutProgressResponse,
  ExerciseProgressResponse,
  ExerciseStatsResponse,
  ExerciseVariationsResponse,
  ExerciseAlternativesResponse,
  CreateVariationInput,
  CsvImportResult,
  ExerciseHistoryEntry,
  TrainingLabel,
  UpdateExercisePrInput,
} from './types';
import { ApiError } from './api';

export interface ExerciseSearchParams {
  q?: string;
  muscleGroup?: string;
  category?: string;
  equipment?: string;
  difficulty?: string;
  movementPattern?: string;
  limit?: number;
  offset?: number;
  parentOnly?: boolean;
  parentExerciseId?: number;
}

export interface ExerciseSearchResult {
  exercises: Exercise[];
  totalCount: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface ParsedApiError {
  status: number;
  message: string;
  body?: ApiErrorBody;
}

export class ExerciseService {
  private static readonly ENDPOINT = '/api/exercises';

  static async getAll(): Promise<Exercise[]> {
    // Add pagination parameter to get all exercises (default is 30)
    const response = await apiClient.get<ApiCollection<Exercise>>(`${this.ENDPOINT}?pagination=false`);
    return response.data['member'];
  }

  static async searchExercises(params: ExerciseSearchParams = {}): Promise<ExerciseSearchResult> {
    const queryParams = new URLSearchParams();
    
    if (params.q) queryParams.append('q', params.q);
    if (params.muscleGroup) queryParams.append('muscleGroup', params.muscleGroup);
    if (params.category) queryParams.append('category', params.category);
    if (params.equipment) queryParams.append('equipment', params.equipment);
    if (params.difficulty) queryParams.append('difficulty', params.difficulty);
    if (params.movementPattern) queryParams.append('movementPattern', params.movementPattern);
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.parentOnly) queryParams.append('parentOnly', 'true');
    if (params.parentExerciseId) queryParams.append('parentExerciseId', params.parentExerciseId.toString());
    
    const response = await apiClient.get<ExerciseSearchResult>(
      `/api/exercise-management/search?${queryParams.toString()}`
    );
    return response.data;
  }

  static async getById(id: number): Promise<Exercise> {
    const response = await apiClient.get<Exercise>(`${this.ENDPOINT}/${id}`);
    return response.data;
  }

  static async create(exercise: CreateExerciseInput): Promise<Exercise> {
    const response = await apiClient.post<Exercise>(this.ENDPOINT, exercise);
    return response.data;
  }

  static async update(id: number, exercise: Partial<CreateExerciseInput>): Promise<Exercise> {
    const response = await apiClient.patch<Exercise>(`${this.ENDPOINT}/${id}`, exercise);
    return response.data;
  }

  static async delete(id: number): Promise<void> {
    await apiClient.delete(`${this.ENDPOINT}/${id}`);
  }

  // Utility methods for filtering (server-side via searchExercises)
  static async getByMuscleGroup(muscleGroup: string): Promise<Exercise[]> {
    const result = await this.searchExercises({ muscleGroup, limit: 500 });
    return result.exercises;
  }

  static async getByCategory(category: string): Promise<Exercise[]> {
    const result = await this.searchExercises({ category, limit: 500 });
    return result.exercises;
  }

  static async getByDifficulty(difficulty: string): Promise<Exercise[]> {
    const result = await this.searchExercises({ difficulty, limit: 500 });
    return result.exercises;
  }

  static async getMuscleGroups(): Promise<string[]> {
    const response = await apiClient.get<string[]>(`/api/exercise-management/muscle-groups`);
    return response.data;
  }

  static async getCategories(): Promise<string[]> {
    const response = await apiClient.get<string[]>(`/api/exercise-management/categories`);
    return response.data;
  }

  static async getEquipmentTypes(): Promise<string[]> {
    const response = await apiClient.get<string[]>(`/api/exercise-management/equipment`);
    return response.data;
  }

  static async getMovementPatterns(): Promise<string[]> {
    const response = await apiClient.get<string[]>(`/api/exercise-management/movement-patterns`);
    return response.data;
  }

  static async bulkCreate(exercises: CreateExerciseInput[]): Promise<{ message: string; exercises: string[] }> {
    const response = await apiClient.post<{ message: string; exercises: string[] }>(`/api/exercise-management/bulk`, { exercises });
    return response.data;
  }

  static async importCommonExercises(): Promise<{ message: string; totalAvailable: number }> {
    const response = await apiClient.post<{ message: string; totalAvailable: number }>(`/api/exercise-management/import/common`, {});
    return response.data;
  }

  static async downloadCsvTemplate(): Promise<void> {
    await apiClient.downloadFile('/api/exercise-management/csv-template', 'exercises_template.csv');
  }

  static async exportCsv(): Promise<void> {
    await apiClient.downloadFile('/api/exercise-management/export/csv', 'exercises_export.csv');
  }

  static async importCsv(file: File): Promise<CsvImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    const { API_BASE_URL } = await import('../config');
    const response = await fetch(`${API_BASE_URL}/api/exercise-management/import/csv`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  // Helper method to get muscle group color
  static getMuscleGroupColor(muscleGroup: string): string {
    const colorMap: { [key: string]: string } = {
      'chest': '#FF6B6B',     // Red
      'back': '#4ECDC4',      // Teal
      'quadriceps': '#45B7D1', // Blue
      'hamstrings': '#5D9CEC', // Lighter Blue
      'adductors': '#7FC8D8',  // Cyan
      'shoulders': '#FFA07A',  // Light Salmon
      'arms': '#98D8C8',      // Mint
      'core': '#FFCE56',      // Yellow
      'cardio': '#F7B731',    // Orange
      'glutes': '#FF9FF3',    // Pink
      'calves': '#A8E6CF',    // Light Green
    };
    return colorMap[muscleGroup.toLowerCase()] || '#95A5A6'; // Gray as default
  }

  // Helper method to get difficulty color
  static getDifficultyColor(difficulty: string): string {
    const colorMap: { [key: string]: string } = {
      'beginner': '#2ECC71',    // Green
      'intermediate': '#F39C12', // Orange
      'advanced': '#E74C3C',    // Red
    };
    return colorMap[difficulty.toLowerCase()] || '#95A5A6';
  }

  // Fetch last workout data for an exercise
  static async getLastWorkoutData(exerciseId: number): Promise<{
    summary: string | null;
    sets: Array<{ reps: number; weight: number; assistance: boolean | null; rpe: number | null; repsLeft?: number | null; repsRight?: number | null }>;
    workoutDate: string;
  } | null> {
    try {
      const response = await apiClient.get<{
        summary: string | null;
        sets: Array<{ reps: number; weight: number; assistance: boolean | null; rpe: number | null; repsLeft?: number | null; repsRight?: number | null }>;
        workoutDate: string;
      }>(`/api/exercises/${exerciseId}/last-workout`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch last workout data:', err);
      return null;
    }
  }

  // Fetch exercises that have been done in completed workouts
  static async getExercisesWithHistory(programId?: number): Promise<ExerciseHistoryEntry[]> {
    const queryParams = new URLSearchParams();
    if (programId !== undefined) queryParams.append('programId', programId.toString());
    const qs = queryParams.toString();
    const response = await apiClient.get<{ exercises: ExerciseHistoryEntry[] }>(
      `/api/workout-progress/exercises-with-history${qs ? '?' + qs : ''}`
    );
    return response.data.exercises;
  }

  // Fetch available program days for progress filtering
  static async getAvailableProgramDays(programId?: number): Promise<number[]> {
    const queryParams = new URLSearchParams();
    if (programId !== undefined) queryParams.append('programId', programId.toString());
    const qs = queryParams.toString();
    const response = await apiClient.get<{ days: number[] }>(`/api/workout-progress/available-days${qs ? '?' + qs : ''}`);
    return response.data.days;
  }

  // Fetch workout progress, optionally filtered by program day, date range, and gym
  static async getWorkoutProgress(day?: number, dateFrom?: string, dateTo?: string, gymId?: number, trainingLabel?: TrainingLabel | null, programId?: number): Promise<WorkoutProgressResponse> {
    const queryParams = new URLSearchParams();
    if (day !== undefined) queryParams.append('day', day.toString());
    if (dateFrom) queryParams.append('dateFrom', dateFrom);
    if (dateTo) queryParams.append('dateTo', dateTo);
    if (gymId) queryParams.append('gymId', gymId.toString());
    if (trainingLabel) queryParams.append('trainingLabel', trainingLabel);
    if (programId !== undefined) queryParams.append('programId', programId.toString());
    const qs = queryParams.toString();
    const response = await apiClient.get<WorkoutProgressResponse>(`/api/workout-progress${qs ? '?' + qs : ''}`);
    return this.parseWorkoutProgressResponse(response.data);
  }

  // Fetch exercise-specific progress, optionally filtered by program day, date range, and gym
  static async getExerciseProgress(exerciseId: number, day?: number, includeVariations?: boolean, dateFrom?: string, dateTo?: string, gymId?: number, trainingLabel?: TrainingLabel | null, programId?: number): Promise<ExerciseProgressResponse> {
    const queryParams = new URLSearchParams();
    if (day !== undefined) queryParams.append('day', day.toString());
    if (includeVariations) queryParams.append('includeVariations', 'true');
    if (dateFrom) queryParams.append('dateFrom', dateFrom);
    if (dateTo) queryParams.append('dateTo', dateTo);
    if (gymId) queryParams.append('gymId', gymId.toString());
    if (trainingLabel) queryParams.append('trainingLabel', trainingLabel);
    if (programId !== undefined) queryParams.append('programId', programId.toString());
    const qs = queryParams.toString();
    const response = await apiClient.get<ExerciseProgressResponse>(`/api/exercises/${exerciseId}/progress${qs ? '?' + qs : ''}`);
    return this.parseExerciseProgressResponse(response.data);
  }

  static async getExerciseStats(params: {
    day?: number;
    dateFrom?: string;
    dateTo?: string;
    gymId?: number;
    programId?: number;
    exerciseId?: number;
    muscleGroup?: string;
    includeVariations?: boolean;
    trainingLabel?: TrainingLabel | null;
  } = {}): Promise<ExerciseStatsResponse> {
    const queryParams = new URLSearchParams();

    if (params.day !== undefined) queryParams.append('day', params.day.toString());
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.gymId !== undefined) queryParams.append('gymId', params.gymId.toString());
    if (params.programId !== undefined) queryParams.append('programId', params.programId.toString());
    if (params.exerciseId !== undefined) queryParams.append('exerciseId', params.exerciseId.toString());
    if (params.muscleGroup) queryParams.append('muscleGroup', params.muscleGroup);
    if (params.includeVariations !== undefined) queryParams.append('includeVariations', params.includeVariations ? 'true' : 'false');
    if (params.trainingLabel) queryParams.append('trainingLabel', params.trainingLabel);

    const qs = queryParams.toString();
    const response = await apiClient.get<ExerciseStatsResponse>(`/api/workout-progress/exercise-stats${qs ? '?' + qs : ''}`);
    return response.data;
  }

  // Fetch workout progress filtered by muscle group
  static async getWorkoutProgressByMuscleGroup(muscleGroup: string, dateFrom?: string, dateTo?: string, gymId?: number, trainingLabel?: TrainingLabel | null, programId?: number): Promise<WorkoutProgressResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('muscleGroup', muscleGroup);
    if (dateFrom) queryParams.append('dateFrom', dateFrom);
    if (dateTo) queryParams.append('dateTo', dateTo);
    if (gymId) queryParams.append('gymId', gymId.toString());
    if (trainingLabel) queryParams.append('trainingLabel', trainingLabel);
    if (programId !== undefined) queryParams.append('programId', programId.toString());
    const response = await apiClient.get<WorkoutProgressResponse>(`/api/workout-progress/by-muscle-group?${queryParams.toString()}`);
    return this.parseWorkoutProgressResponse(response.data);
  }

  static async getExerciseRecords(exerciseId: number): Promise<ExerciseRecordsResponse> {
    const response = await apiClient.get<ExerciseRecordsResponse>(`/api/exercises/${exerciseId}/records`);
    return this.parseExerciseRecordsResponse(response.data);
  }

  static async updateExercisePr(exerciseId: number, input: UpdateExercisePrInput): Promise<ExerciseRecordsResponse> {
    const response = await apiClient.put<ExerciseRecordsResponse>(`/api/exercises/${exerciseId}/records/pr`, input);
    return this.parseExerciseRecordsResponse(response.data);
  }

  static parseApiError(error: unknown): ParsedApiError {
    if (error instanceof ApiError) {
      const parsedBody = this.tryParseErrorBody(error.message);
      return {
        status: error.status,
        message: parsedBody?.message ?? error.message,
        body: parsedBody,
      };
    }

    if (error instanceof Error) {
      return {
        status: 500,
        message: error.message,
      };
    }

    return {
      status: 500,
      message: 'Unknown error',
    };
  }

  // Get all variations of an exercise
  static async getVariations(exerciseId: number): Promise<ExerciseVariationsResponse> {
    const response = await apiClient.get<ExerciseVariationsResponse>(
      `/api/exercise-management/${exerciseId}/variations`
    );
    return response.data;
  }

  // Create a variation of an exercise
  static async createVariation(exerciseId: number, data: CreateVariationInput): Promise<{ id: number; exercise: Exercise; message: string }> {
    const response = await apiClient.post<{ id: number; exercise: Exercise; message: string }>(
      `/api/exercise-management/${exerciseId}/variations`,
      data
    );
    return response.data;
  }

  // Get interchangeable (swappable) alternatives for an exercise
  static async getAlternatives(exerciseId: number): Promise<ExerciseAlternativesResponse> {
    const response = await apiClient.get<ExerciseAlternativesResponse>(
      `/api/exercise-management/${exerciseId}/alternatives`
    );
    return response.data;
  }

  // Link another exercise as an alternative (symmetric on the backend)
  static async addAlternative(exerciseId: number, alternativeId: number): Promise<ExerciseAlternativesResponse> {
    const response = await apiClient.post<ExerciseAlternativesResponse>(
      `/api/exercise-management/${exerciseId}/alternatives`,
      { alternativeId }
    );
    return response.data;
  }

  // Unlink an alternative (symmetric on the backend)
  static async removeAlternative(exerciseId: number, alternativeId: number): Promise<void> {
    await apiClient.delete(`/api/exercise-management/${exerciseId}/alternatives/${alternativeId}`);
  }

  private static parseWorkoutProgressResponse(response: WorkoutProgressResponse): WorkoutProgressResponse {
    return {
      workouts: Array.isArray(response?.workouts)
        ? response.workouts.map((workout) => ({
            ...workout,
            exercises: Array.isArray(workout.exercises)
              ? workout.exercises.map((exercise) => ({
                  ...exercise,
                  currentE1rm: this.parseNullableNumber(exercise.currentE1rm),
                  currentPr: this.parseNullableNumber(exercise.currentPr),
                }))
              : [],
          }))
        : [],
    };
  }

  private static parseExerciseProgressResponse(response: ExerciseProgressResponse): ExerciseProgressResponse {
    return {
      workouts: Array.isArray(response?.workouts)
        ? response.workouts.map((workout) => ({
            ...workout,
            currentE1rm: this.parseNullableNumber(workout.currentE1rm),
            currentPr: this.parseNullableNumber(workout.currentPr),
          }))
        : [],
    };
  }

  private static parseExerciseRecordsResponse(response: ExerciseRecordsResponse): ExerciseRecordsResponse {
    return {
      current: {
        e1rm: this.parseExerciseRecordEntry(response?.current?.e1rm ?? null),
        pr: this.parseExerciseRecordEntry(response?.current?.pr ?? null),
      },
      history: {
        e1rm: Array.isArray(response?.history?.e1rm)
          ? response.history.e1rm.map((entry) => this.parseExerciseRecordEntry(entry)).filter(Boolean) as ExerciseRecordsResponse['history']['e1rm']
          : [],
        pr: Array.isArray(response?.history?.pr)
          ? response.history.pr.map((entry) => this.parseExerciseRecordEntry(entry)).filter(Boolean) as ExerciseRecordsResponse['history']['pr']
          : [],
      },
    };
  }

  private static parseExerciseRecordEntry<T extends { value: number; sourceSetId: number | null } | null>(entry: T): T {
    if (!entry) {
      return entry;
    }

    return {
      ...entry,
      value: Number(entry.value),
      sourceSetId: entry.sourceSetId === null || entry.sourceSetId === undefined ? null : Number(entry.sourceSetId),
    };
  }

  private static parseNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private static tryParseErrorBody(message: string): ApiErrorBody | undefined {
    const separator = ': ';
    const index = message.indexOf(separator);
    if (index === -1) {
      return undefined;
    }

    const rawBody = message.slice(index + separator.length);
    try {
      return JSON.parse(rawBody) as ApiErrorBody;
    } catch (_error) {
      return undefined;
    }
  }
}

export default ExerciseService;
